// GrowthInstructions.tsx
import React, { useState, useEffect } from 'react';
import { cn } from '../utils/cn';
import { updateSectionLocation } from '../lib/updateSectionLocation';
import useSectionLayout from '../hooks/useSectionLayout';
import { supabase } from '../lib/supabase';
import { Button } from './Button';
import { ChevronDown, Rocket } from 'lucide-react';

export default function GrowthInstructions() {
  const [open, setOpen] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const { locations } = useSectionLayout();
  const [userName, setUserName] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', user.id)
          .single();

        if (profile?.name) {
          setUserName(profile.name.split(' ')[0]);
        }
      } catch (error) {
        console.error('Error fetching name:', error);
      }
    };
    fetchUserData();
  }, []);

  const currentLocation = locations?.growth_instructions ?? 'loop';

  const handleToggleLayout = async () => {
    const target = currentLocation === 'tools' ? 'loop' : 'tools';
    setIsToggling(true);
    try {
      await updateSectionLocation('growth_instructions', target);
      window.location.reload();
    } catch (error) {
      console.error('Error updating section location:', error);
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <div className="border rounded-xl overflow-hidden bg-white shadow-lg">
      <button
        onClick={() => setOpen(prev => !prev)}
        className="w-full flex justify-between items-center px-4 py-6 text-left hover:bg-blue-50"
      >
        <div className="flex items-center gap-2 text-[#004060] font-semibold text-xl">
          <Rocket className="w-5 h-5 text-[#004060]" />
          Getting Started
        </div>

        <ChevronDown
          className={cn(
            'w-5 h-5 text-[#236caf] transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <div className="p-4 sm:p-6 space-y-4 text-med text-gray-800">
          <p>
            {userName ? `Welcome, ${userName}.` : 'Welcome.'} The real power of this app is simple — it's here to help you harness your thoughts and use your fears so you can do great things. Below are a few quick tips to get started.
          </p>

          <ul className="list-disc list-inside ml-6 mr-6 space-y-6">
            <li>
              <strong>Add this app to your home screen:</strong> On your phone, go to app.freelie.net. Once there, hit the share icon (the square with the arrow) at the bottom of your phone and tap "Add to Home Screen." That'll pin the blue mountain goat right where you need it.
            </li>

            <li>
              <strong>The Alignment Engine and the Planner serve different but connected purposes.</strong> The Alignment Engine is for creating clarity, energy, and aligned daily actions. The Planner is for designing long-term goals and the paths that lead to them. They work best when each does its own job and supports the other.
            </li>

            <li>
              <strong>Before creating a long-term goal, start with the Alignment Engine.</strong> Rather than jumping straight to an outcome, use a goal like, "Create a goal for my [XYZ] area." This helps ensure the goal is grounded in your values rather than driven by pressure, comparison, or hidden fear. Goals created this way tend to feel clearer and more sustainable.
            </li>

            <li>
              <strong>It's also useful to use the Alignment Engine before creating a plan.</strong> Doubt and fear can quietly shape planning, even when we don't notice it. Running the Engine first helps you create ladders from a grounded place instead of a reactive one.
            </li>

            <li>
              <strong>Plans are built using reverse ladders.</strong> Each ladder has five steps that work backward from the goal to where you are now. The steps describe conditions that must be true, not tasks. If five steps aren't enough to get you to actions you can take today, you create a nested ladder. A nested ladder starts from your current reality and ends at the first step of the ladder above it. You can create as many nested ladders as you need.
            </li>

            <li>
              <strong>The deepest ladder becomes your Active Ladder.</strong> This is the ladder you are working on right now. The current step of that ladder supplies the goal inside the Alignment Engine, and the actions you choose there appear in the Action section. As you complete actions and steps, focus naturally moves up through the ladders toward the original goal.
            </li>

            <li>
              <strong>If you get stuck on a step or an action, return to the Alignment Engine.</strong> You'll see the relevant ladder step available as a goal option, making it easy to restore alignment in that specific area. Don't hesitate to use the Engine more often than you think makes sense. Each pass tends to surface new insight and bring a noticeable boost in energy.
            </li>

            <li>
              <strong>When frustration or anger blocks progress, give yourself more space.</strong> Use the Alignment Engine with a goal like, "Create a plan to get past this frustration," or even, "Create a plan to create a plan." That distance often makes movement possible again, even if it takes a few repetitions.
            </li>

            <li>
              <strong>After taking an action connected to a ladder step, you can check it off in either the Planner or the Action section.</strong> When actions are taken from your values, learning follows. When you notice an idea, insight, or hard-earned lesson, capture it in the Lessons section. You can simply dictate your thoughts without worrying about polish. One idea a day is enough to start.
            </li>

            <li>
              <strong>Lessons can also be a place to vent.</strong> If you feel angry or overwhelmed, speaking it out clears mental weight, and there's often something useful on the other side. Everything you record becomes part of your Gold File, a growing record of lived wisdom that you can later use with AI to spot patterns, plan next steps, or create meaningful content based on your real experience.
            </li>
           
              <li>
              <strong>The Vision Docs:</strong> The best way to create your docs is by using your Gold File. Capture your thoughts and lessons learned for a week or so, download your Gold File, and then ask ChatGPT or your favorite LLM to use that data to create a personal and professional vision document for you. The results won't be perfect the first time but it will lay the foundation and help you get started. Review the docs weekly and make changes until your are excited and inspired to read them.
            </li>
             

            <li>
              <strong>Keep showing up:</strong> This system works through repetition. Journal. Act. Reflect. Repeat. That's how you stay focused, aligned, and ready to do great things.
            </li>
          </ul>

          <p>
            Once you're familiar with this, feel free to move these tips to the Tools page to keep your dashboard clean and focused.
          </p>

          <div className="pt-4 border-t border-gray-200">
            <Button
              onClick={handleToggleLayout}
              disabled={isToggling}
              variant="outline"
              className="w-full sm:w-auto text-sm"
            >
              {isToggling
                ? 'Moving...'
                : `Move to ${currentLocation === 'tools' ? 'Loop' : 'Tools'}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}